<template>
  <div class="configuracion-usuarios py-4" :class="themeClass">
    <div class="container-fluid">
      <div class="row justify-content-center">
        <div class="col-xxl-10 col-xl-11">
          <div class="card shadow-sm border-0">
            <div class="card-header bg-gradient text-white d-flex justify-content-between align-items-center">
              <div>
                <h3 class="mb-0"><i class="bi bi-people me-2"></i>Usuarios de la Empresa</h3>
                <small class="text-white-50">Crea usuarios y asigna su rol. El rol (en Roles y permisos) define que puede ver y hacer cada uno.</small>
              </div>
              <button v-if="puedeCrear" class="btn btn-light btn-sm" @click="abrirModalNuevo" :disabled="sinCupoOPlan">
                <i class="bi bi-person-plus me-1"></i> Nuevo usuario
              </button>
            </div>
            <div class="card-body">

              <!-- Banner de limites del plan -->
              <div v-if="limites" class="alert mb-3 d-flex justify-content-between align-items-center"
                   :class="sinCupoOPlan ? 'alert-warning' : 'alert-info'">
                <div>
                  <strong>{{ limites.plan_nombre || 'Sin plan activo' }}</strong>
                  <span v-if="limites.permite_gestion_usuarios">
                    &middot; {{ limites.usados }} de {{ limites.max_usuarios !== null ? limites.max_usuarios : 'ilimitado' }} usuarios usados
                  </span>
                  <span v-else> &middot; este plan no permite gestión de usuarios.</span>
                </div>
                <span v-if="sinCupoOPlan" class="fw-semibold">
                  <i class="bi bi-exclamation-triangle me-1"></i>Sin cupo para nuevos usuarios.
                </span>
              </div>

              <div v-if="cargando" class="text-center py-5">
                <div class="spinner-border text-primary"></div>
                <p class="text-muted mt-3">Cargando usuarios...</p>
              </div>

              <template v-else>
                <div v-if="mensajeError" class="alert alert-danger d-flex justify-content-between align-items-center">
                  <span>{{ mensajeError }}</span>
                  <button type="button" class="btn-close" @click="mensajeError = ''"></button>
                </div>
                <div v-if="mensajeExito" class="alert alert-success d-flex justify-content-between align-items-center">
                  <span>{{ mensajeExito }}</span>
                  <button type="button" class="btn-close" @click="mensajeExito = ''"></button>
                </div>

                <!-- Barra de busqueda -->
                <div class="row mb-3 align-items-center">
                  <div class="col-md-6">
                    <div class="input-group">
                      <span class="input-group-text"><i class="bi bi-search"></i></span>
                      <input type="text" class="form-control" v-model="busqueda"
                             placeholder="Buscar por nombre o correo..." />
                    </div>
                  </div>
                  <div class="col-md-3">
                    <select class="form-select" v-model="filtroEstado">
                      <option value="">Todos los estados</option>
                      <option value="1">Activos</option>
                      <option value="0">Inactivos</option>
                    </select>
                  </div>
                  <div class="col-md-3 text-end">
                    <small class="text-muted">{{ usuariosFiltrados.length }} usuario(s)</small>
                  </div>
                </div>

                <!-- Tabla de usuarios -->
                <div class="table-responsive">
                  <table class="table align-middle">
                    <thead class="table-light">
                      <tr>
                        <th>Usuario</th>
                        <th>Correo</th>
                        <th>Roles</th>
                        <th class="text-center">Estado</th>
                        <th class="text-center">Último acceso</th>
                        <th class="text-center" style="width: 190px;">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="usuariosFiltrados.length === 0">
                        <td colspan="6" class="text-center text-muted py-5">
                          <i class="bi bi-people fs-1 d-block mb-2"></i>
                          <span v-if="busqueda || filtroEstado">No hay usuarios que coincidan con los filtros.</span>
                          <span v-else>Aún no hay usuarios registrados. Crea el primero para comenzar.</span>
                        </td>
                      </tr>
                      <tr v-for="usuario in usuariosFiltrados" :key="usuario.id">
                        <td>
                          <div class="d-flex align-items-center gap-2">
                            <div class="avatar-circle" :style="{ backgroundColor: colorAvatar(usuario.nombre) }">
                              {{ iniciales(usuario.nombre) }}
                            </div>
                            <div>
                              <div class="fw-semibold">{{ usuario.nombre }}</div>
                              <small class="text-muted">{{ usuario.nombreUsuario || '—' }}</small>
                            </div>
                          </div>
                        </td>
                        <td>{{ usuario.correo }}</td>
                        <td>
                          <span v-if="usuario.esAdministrador" class="badge bg-danger me-1">Administrador</span>
                          <template v-else>
                            <span v-if="!usuario.rolNombres || !usuario.rolNombres.length" class="badge bg-secondary">Sin rol</span>
                            <span v-for="(r, i) in usuario.rolNombres" :key="i" class="badge bg-primary me-1">{{ r }}</span>
                          </template>
                        </td>
                        <td class="text-center">
                          <div class="form-check form-switch d-flex justify-content-center">
                            <input class="form-check-input" type="checkbox"
                                   :checked="!!usuario.activo"
                                   @change="toggleEstado(usuario)"
                                   :disabled="usuario.esActualUsuario || !puedeEditar">
                          </div>
                        </td>
                        <td class="text-center">
                          <small class="text-muted">{{ formatearFecha(usuario.ultimoAcceso) }}</small>
                        </td>
                        <td class="text-center">
                          <button v-if="puedeEditar" class="btn btn-outline-primary btn-sm me-1" @click="abrirModalEditar(usuario)" title="Editar">
                            <i class="bi bi-pencil"></i>
                          </button>
                          <button v-if="puedeEditar" class="btn btn-outline-secondary btn-sm me-1" @click="abrirAsignarRol(usuario)"
                                  :disabled="usuario.esAdministrador" title="Asignar roles">
                            <i class="bi bi-shield-check"></i>
                          </button>
                          <button v-if="puedeEliminar" class="btn btn-outline-danger btn-sm" @click="confirmarEliminar(usuario)"
                                  :disabled="usuario.esActualUsuario" title="Eliminar">
                            <i class="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal crear/editar usuario -->
    <div class="modal fade" :class="{ show: mostrarModal }" :style="{ display: mostrarModal ? 'block' : 'none' }"
         tabindex="-1" @click.self="cerrarModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content" :class="themeClass">
          <div class="modal-header bg-primary text-white">
            <h5 class="modal-title">
              <i class="bi bi-person-gear me-2"></i>
              {{ modoEdicion ? 'Editar usuario' : 'Nuevo usuario' }}
            </h5>
            <button type="button" class="btn-close btn-close-white" @click="cerrarModal"></button>
          </div>
          <div class="modal-body">

            <div v-if="errorModal" class="alert alert-danger">{{ errorModal }}</div>

            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Nombre completo *</label>
                <input type="text" class="form-control" v-model.trim="formulario.nombre"
                       placeholder="Ej. María González" />
              </div>
              <div class="col-md-6">
                <label class="form-label">Correo electrónico *</label>
                <input type="email" class="form-control" v-model.trim="formulario.correo"
                       placeholder="usuario@empresa.com"
                       :readonly="modoEdicion && formulario.esActualUsuario" />
              </div>
              <div class="col-md-6">
                <label class="form-label">Nombre de usuario</label>
                <input type="text" class="form-control" v-model.trim="formulario.nombreUsuario"
                       placeholder="Opcional — se generará del correo si se deja vacío" />
              </div>
              <div class="col-md-6">
                <label class="form-label">Rol *</label>
                <select class="form-select" v-model="formulario.rolId">
                  <option value="">Selecciona un rol...</option>
                  <option v-for="rol in rolesCA" :key="rol.id" :value="rol.id">
                    {{ rol.nombre }}
                  </option>
                </select>
                <small v-if="!rolesCA.length" class="text-muted">
                  No hay roles creados. Crea uno primero en <strong>Roles y permisos</strong>.
                </small>
              </div>
              <div class="col-md-6">
                <label class="form-label">{{ modoEdicion ? 'Nueva contraseña' : 'Contraseña *' }}</label>
                <div class="input-group">
                  <input :type="mostrarClave ? 'text' : 'password'" class="form-control"
                         v-model="formulario.clave"
                         :placeholder="modoEdicion ? 'Deja vacío para no cambiar' : 'Mínimo 8 caracteres'" />
                  <button class="btn btn-outline-secondary" type="button" @click="mostrarClave = !mostrarClave">
                    <i :class="mostrarClave ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                  </button>
                </div>
                <small class="text-muted" v-if="formulario.clave">
                  {{ indicadorFuerzaClave }}
                </small>
              </div>
              <div class="col-md-6" v-if="formulario.clave">
                <label class="form-label">Confirmar contraseña *</label>
                <input :type="mostrarClave ? 'text' : 'password'" class="form-control"
                       v-model="formulario.confirmarClave"
                       placeholder="Repite la contraseña" />
              </div>
            </div>

            <!-- Descripción del rol seleccionado -->
            <div class="mt-3 p-3 rounded" style="background: rgba(17,115,212,0.06);" v-if="descripcionRolSeleccionado">
              <small class="text-muted">{{ descripcionRolSeleccionado }}</small>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" @click="cerrarModal">Cancelar</button>
            <button type="button" class="btn btn-primary" @click="guardarUsuario" :disabled="guardando">
              <span v-if="guardando" class="spinner-border spinner-border-sm me-1"></span>
              {{ modoEdicion ? 'Guardar cambios' : 'Crear usuario' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="mostrarModal"></div>

    <!-- Modal asignar roles -->
    <div v-if="modalRoles" class="modal-fondo" @click.self="modalRoles = null">
      <div class="modal-flotante" :class="themeClass">
        <h5 class="mb-1">Roles para "{{ modalRoles.usuario.correo }}"</h5>
        <p class="text-muted small mb-3">Selecciona los roles del nuevo sistema de control de acceso.</p>
        <div v-if="!rolesCA.length" class="text-muted small">
          No hay roles definidos. Crea uno primero en <strong>Roles y permisos</strong>.
        </div>
        <div v-else class="roles-lista">
          <label v-for="r in rolesCA" :key="r.id" class="rol-check">
            <input type="checkbox" :value="r.id" v-model="modalRoles.seleccion" />
            <span><strong>{{ r.nombre }}</strong> <small class="text-muted">{{ r.codigo }}</small></span>
          </label>
        </div>
        <div class="modal-acciones mt-3">
          <button class="btn btn-outline-secondary btn-sm" @click="modalRoles = null">Cancelar</button>
          <button class="btn btn-primary btn-sm" @click="guardarRoles" :disabled="guardando">Guardar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import themeManager from '@/store/theme'
import api from '@/services/api'
import controlAcceso from '@/services/controlAccesoService'
import { notifySuccess, notifyError } from '@/services/notificationService'

export default {
  name: 'ClientConfiguracionUsuarios',
  data() {
    return {
      cargando: true,
      guardando: false,
      mensajeError: '',
      mensajeExito: '',
      busqueda: '',
      filtroEstado: '',
      usuarios: [],
      rolesCA: [],
      limites: null,
      mostrarModal: false,
      modoEdicion: false,
      mostrarClave: false,
      errorModal: '',
      formulario: {
        id: null,
        nombre: '',
        correo: '',
        nombreUsuario: '',
        rolId: '',
        clave: '',
        confirmarClave: '',
        esActualUsuario: false
      },
      modalRoles: null,
    }
  },
  computed: {
    themeClass() {
      return themeManager.themeClass
    },
    puedeCrear() {
      return this.$store.getters.puedoHacer('/control-acceso/usuarios', 'crear')
    },
    puedeEditar() {
      return this.$store.getters.puedoHacer('/control-acceso/usuarios', 'editar')
    },
    puedeEliminar() {
      return this.$store.getters.puedoHacer('/control-acceso/usuarios', 'eliminar')
    },
    sinCupoOPlan() {
      if (!this.limites) return false
      if (!this.limites.permite_gestion_usuarios) return true
      if (this.limites.max_usuarios === null) return false
      return this.limites.usados >= this.limites.max_usuarios
    },
    usuariosFiltrados() {
      return this.usuarios.filter(u => {
        const termino = this.busqueda.toLowerCase()
        const coincideTexto = !termino || (
          (u.nombre || '').toLowerCase().includes(termino) ||
          (u.correo || '').toLowerCase().includes(termino) ||
          (u.nombreUsuario || '').toLowerCase().includes(termino)
        )
        const coincideEstado = !this.filtroEstado || String(u.activo ? '1' : '0') === this.filtroEstado
        return coincideTexto && coincideEstado
      })
    },
    descripcionRolSeleccionado() {
      const rol = this.rolesCA.find(r => r.id === this.formulario.rolId)
      return rol?.descripcion || ''
    },
    indicadorFuerzaClave() {
      const clave = this.formulario.clave
      if (!clave) return ''
      if (clave.length < 8) return '⚠️ Muy corta (mínimo 8 caracteres)'
      const puntaje = [/[0-9]/.test(clave), /[A-Z]/.test(clave), /[^a-zA-Z0-9]/.test(clave)].filter(Boolean).length
      if (puntaje === 3) return '✅ Contraseña fuerte'
      if (puntaje === 2) return '🟡 Contraseña aceptable'
      return '🔴 Contraseña débil — añade números, mayúsculas o símbolos'
    }
  },
  created() {
    this.cargarDatos()
  },
  methods: {
    async cargarDatos() {
      this.cargando = true
      this.mensajeError = ''
      try {
        const [resConf, resCA, resRoles, resLimites] = await Promise.all([
          api.get('/configuracion/usuarios').catch(() => null),
          controlAcceso.listarUsuarios().catch(() => null),
          controlAcceso.listarRoles().catch(() => null),
          controlAcceso.limitesUsuarios().catch(() => null),
        ])

        const confList = Array.isArray(resConf?.data?.data) ? resConf.data.data
          : Array.isArray(resConf?.data) ? resConf.data : []
        const caList = Array.isArray(resCA?.data) ? resCA.data : []

        // Indice de datos del nuevo sistema por id
        const caMap = new Map(caList.map(u => [u.id, u]))

        this.usuarios = confList.map(u => {
          const ca = caMap.get(u.id) || {}
          return {
            id:              u.id,
            nombre:          u.nombre || u.name || '',
            correo:          u.correo || u.email || '',
            nombreUsuario:   u.nombre_usuario || u.username || '',
            activo:          u.activo !== false && u.is_active !== false,
            ultimoAcceso:    u.ultimo_acceso || u.last_login || null,
            esActualUsuario: !!u.es_actual,
            rolNombres:      ca.rol_nombres || [],
            rolIds:          ca.rol_ids || [],
            esAdministrador: !!ca.es_administrador,
          }
        })

        this.rolesCA = Array.isArray(resRoles?.data) ? resRoles.data : []
        this.limites = resLimites?.data || null
      } catch (error) {
        this.mensajeError = 'No se pudo cargar la información de usuarios.'
      } finally {
        this.cargando = false
      }
    },

    // --- Crear / editar usuario ---

    abrirModalNuevo() {
      this.modoEdicion = false
      this.errorModal = ''
      this.mostrarClave = false
      this.formulario = { id: null, nombre: '', correo: '', nombreUsuario: '', rolId: '', clave: '', confirmarClave: '', esActualUsuario: false }
      this.mostrarModal = true
    },

    abrirModalEditar(usuario) {
      this.modoEdicion = true
      this.errorModal = ''
      this.mostrarClave = false
      this.formulario = {
        id:              usuario.id,
        nombre:          usuario.nombre,
        correo:          usuario.correo,
        nombreUsuario:   usuario.nombreUsuario,
        rolId:           usuario.rolIds?.[0] || '',
        clave:           '',
        confirmarClave:  '',
        esActualUsuario: usuario.esActualUsuario
      }
      this.mostrarModal = true
    },

    cerrarModal() {
      this.mostrarModal = false
    },

    async guardarUsuario() {
      this.errorModal = ''
      if (!this.formulario.nombre.trim()) { this.errorModal = 'El nombre es obligatorio.'; return }
      if (!this.formulario.correo.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.formulario.correo)) {
        this.errorModal = 'Ingresa un correo electrónico válido.'; return
      }
      if (!this.formulario.rolId) { this.errorModal = 'Selecciona un rol.'; return }
      if (!this.modoEdicion && !this.formulario.clave) { this.errorModal = 'La contraseña es obligatoria.'; return }
      if (this.formulario.clave && this.formulario.clave.length < 8) { this.errorModal = 'Mínimo 8 caracteres.'; return }
      if (this.formulario.clave && this.formulario.clave !== this.formulario.confirmarClave) {
        this.errorModal = 'Las contraseñas no coinciden.'; return
      }

      this.guardando = true
      try {
        const payload = {
          nombre: this.formulario.nombre,
          correo: this.formulario.correo,
          nombre_usuario: this.formulario.nombreUsuario
        }
        if (this.formulario.clave) payload.clave = this.formulario.clave

        let usuarioId = this.formulario.id
        if (this.modoEdicion) {
          await api.put(`/configuracion/usuarios/${usuarioId}`, payload)
          this.mensajeExito = 'Usuario actualizado correctamente.'
        } else {
          const res = await api.post('/configuracion/usuarios', payload)
          usuarioId = res?.data?.data?.id
          this.mensajeExito = 'Usuario creado correctamente.'
        }
        await controlAcceso.setRolesUsuario(usuarioId, [this.formulario.rolId])
        this.cerrarModal()
        await this.cargarDatos()
      } catch (error) {
        this.errorModal = error?.response?.data?.error || 'No se pudo guardar el usuario.'
      } finally {
        this.guardando = false
      }
    },

    async toggleEstado(usuario) {
      const nuevoEstado = !usuario.activo
      try {
        await api.patch(`/configuracion/usuarios/${usuario.id}/estado`, { activo: nuevoEstado })
        usuario.activo = nuevoEstado
        this.mensajeExito = `Usuario ${nuevoEstado ? 'activado' : 'desactivado'} correctamente.`
      } catch (error) {
        this.mensajeError = error?.response?.data?.error || 'No se pudo cambiar el estado.'
      }
    },

    async confirmarEliminar(usuario) {
      if (!confirm(`¿Eliminar al usuario "${usuario.nombre}"? Esta acción no se puede deshacer.`)) return
      try {
        await api.delete(`/configuracion/usuarios/${usuario.id}`)
        this.mensajeExito = 'Usuario eliminado correctamente.'
        await this.cargarDatos()
      } catch (error) {
        this.mensajeError = error?.response?.data?.error || 'No se pudo eliminar el usuario.'
      }
    },

    // --- Asignar roles (nuevo sistema) ---

    abrirAsignarRol(u) {
      this.modalRoles = { usuario: u, seleccion: [...(u.rolIds || [])] }
    },

    async guardarRoles() {
      this.guardando = true
      try {
        await controlAcceso.setRolesUsuario(this.modalRoles.usuario.id, this.modalRoles.seleccion)
        notifySuccess('Roles actualizados.')
        this.modalRoles = null
        await this.cargarDatos()
      } catch (e) {
        notifyError(e?.response?.data?.error || 'Error al guardar roles.')
      } finally {
        this.guardando = false
      }
    },

    // --- Auxiliares ---

    iniciales(nombre) {
      if (!nombre) return '?'
      return nombre.split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase()
    },

    colorAvatar(nombre) {
      if (!nombre) return '#6c757d'
      const colores = ['#0d6efd','#198754','#dc3545','#ffc107','#0dcaf0','#6f42c1','#fd7e14']
      return colores[nombre.charCodeAt(0) % colores.length]
    },

    formatearFecha(fecha) {
      if (!fecha) return 'Nunca'
      try {
        return new Date(fecha).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' })
      } catch { return fecha }
    }
  }
}
</script>

<style scoped>
.configuracion-usuarios {
  --primary-color: #1173d4;
  --body-bg: #f5f7fb;
  --card-bg: #ffffff;
  --border-color: rgba(17, 115, 212, 0.1);
  --text-primary: #1f2933;
  --text-secondary: #4a5568;
  background-color: var(--body-bg);
  min-height: 100vh;
  padding-bottom: 2rem;
}

.configuracion-usuarios.dark-theme {
  --body-bg: #0b1320;
  --card-bg: #111d30;
  --border-color: rgba(148, 163, 184, 0.18);
  --text-primary: #e2e8f0;
  --text-secondary: #cbd5f5;
}

.card {
  background-color: var(--card-bg);
  border-color: var(--border-color);
  color: var(--text-primary);
}

.modal-content {
  background-color: var(--card-bg);
  color: var(--text-primary);
}

.bg-gradient {
  background: linear-gradient(135deg, rgba(17, 115, 212, 0.95) 0%, rgba(40, 137, 235, 0.9) 100%);
}

.avatar-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
}

.form-control,
.form-select {
  background-color: var(--card-bg);
  color: var(--text-primary);
  border-color: rgba(148, 163, 184, 0.4);
  border-radius: 10px;
}

.form-control:focus,
.form-select:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(17, 115, 212, 0.15);
  background-color: var(--card-bg);
}

.configuracion-usuarios.dark-theme .form-control,
.configuracion-usuarios.dark-theme .form-select {
  background-color: rgba(15, 23, 42, 0.6);
  border-color: rgba(148, 163, 184, 0.3);
  color: var(--text-primary);
}

.table {
  color: var(--text-primary);
}

.table thead th {
  border-color: var(--border-color);
}

.table tbody tr:hover {
  background-color: rgba(17, 115, 212, 0.04);
}

/* Modal flotante de asignacion de roles */
.modal-fondo {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-flotante {
  background: var(--card-bg, #fff);
  color: var(--text-primary, #1f2933);
  border-radius: 12px;
  padding: 1.5rem;
  width: 420px;
  max-width: 95vw;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0,0,0,0.25);
}


.roles-lista {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 280px;
  overflow-y: auto;
}

.rol-check {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid var(--border-color, rgba(17,115,212,0.1));
}

.rol-check:hover {
  background: rgba(17,115,212,0.06);
}

.modal-acciones {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
</style>
